'use client';

import { useEffect, useRef, useState, ReactNode, ElementType } from 'react';
import 'animate.css';

interface AnimateOnViewProps {
  children: ReactNode;
  className?: string;
  animation?: string;
  delay?: number;
  as?: ElementType;
  [key: string]: unknown;
}

export function AnimateOnView({ 
  children, 
  className = '',
  animation = 'animate__fadeInUpBig',
  delay = 0,
  as: Component = 'div',
  ...props
}: AnimateOnViewProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isSmallViewport = window.matchMedia('(max-width: 768px)').matches;

    // Surface content instantly on small viewports or when animations are disabled.
    if (reduceMotion || isSmallViewport) {
      setIsVisible(true);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    let timeoutId: number | undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timeoutId = window.setTimeout(() => {
            setIsVisible(true);
          }, delay);
          observer.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [delay]);

  return (
    <Component
      ref={ref}
      className={`${isVisible ? `animate__animated ${animation}` : 'opacity-0'} ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
}
